import { useCallback, useEffect, useRef } from "react";

interface DragRef {
    preLeft: number | null;
    preTop: number | null;
    x: number;
    y: number;
}

const useDrag = (
    domRef: React.RefObject<HTMLElement>,
    initialPosition: { x: number; y: number },
    setPosition: ({ x, y }: { x: number; y: number }) => void,
) => {
    const ref = useRef<DragRef>({
        preLeft: null,
        preTop: null,
        ...initialPosition,
    });

    const move = useCallback(
        (ev: MouseEvent | TouchEvent) => {
            const screenX =
                ev instanceof MouseEvent ? ev.screenX : ev.touches[0].screenX;
            const screenY =
                ev instanceof MouseEvent ? ev.screenY : ev.touches[0].screenY;

            if (ref.current.preTop !== null) {
                ref.current.x += screenX - (ref.current.preLeft as number);
                ref.current.y += screenY - (ref.current.preTop as number);
                requestAnimationFrame(() => {
                    setPosition({ x: ref.current.x, y: ref.current.y });
                });
            }

            ref.current.preLeft = screenX;
            ref.current.preTop = screenY;
        },
        [setPosition],
    );

    const end = useCallback(() => {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", end);
        window.removeEventListener("touchmove", move);
        window.removeEventListener("touchend", end);
        ref.current.preLeft = ref.current.preTop = null;
    }, [move]);

    const start = useCallback(
        (ev: MouseEvent | TouchEvent) => {
            ev.preventDefault(); // 阻止默认行为
            ref.current.preLeft =
                ev instanceof MouseEvent ? ev.screenX : ev.touches[0].screenX;
            ref.current.preTop =
                ev instanceof MouseEvent ? ev.screenY : ev.touches[0].screenY;
            window.addEventListener("mousemove", move);
            window.addEventListener("mouseup", end);
            window.addEventListener("touchmove", move);
            window.addEventListener("touchend", end);
        },
        [end, move],
    );

    useEffect(() => {
        const element = domRef.current;
        if (element) {
            element.addEventListener("mousedown", start);
            element.addEventListener("touchstart", start);
        }

        return () => {
            const element = domRef.current;
            if (element) {
                element.removeEventListener("mousedown", start);
                element.removeEventListener("touchstart", start);
            }
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", end);
            window.removeEventListener("touchmove", move);
            window.removeEventListener("touchend", end);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domRef.current]);

    return ref;
};

export default useDrag;
